import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslate, useDataProvider } from 'react-admin';
import { DriverProfileWizardFullscreen } from '../../components/DriverProfileWizardFullscreen';
import { BasicDriverInfoStep } from '../../components/wizard-steps/BasicDriverInfoStep';
import { DetailedDriverLicenseStep } from '../../components/wizard-steps/DetailedDriverLicenseStep';
import { DetailedEducationStep } from '../../components/wizard-steps/DetailedEducationStep';
import { DetailedEmploymentStep } from '../../components/wizard-steps/DetailedEmploymentStep';
import { DetailedCertificationStep } from '../../components/wizard-steps/DetailedCertificationStep';
import { SafetyComplianceStep } from '../../components/wizard-steps/SafetyComplianceStep';
import { DocumentsStep } from '../../components/wizard-steps/DocumentsStep';

export const DriverWizard: React.FC = () => {
  const navigate = useNavigate();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [resolvedUserId, setResolvedUserId] = useState<number | undefined>(undefined);

  // Check if editing existing driver or creating for specific user
  // First check URL params, then query params
  let driverId = params.driverId ? parseInt(params.driverId) : (searchParams.get('driverId') ? parseInt(searchParams.get('driverId')!) : undefined);
  let userId = searchParams.get('userId') ? parseInt(searchParams.get('userId')!) : undefined;

  // If we have a driverId but no userId, fetch the driver to get the userId
  useEffect(() => {
    if (driverId && !userId) {
      dataProvider.getOne('drivers', { id: driverId })
        .then((response) => {
          if (response.data && response.data.userId) {
            setResolvedUserId(response.data.userId);
          }
        })
        .catch((error) => {
          console.error('Error fetching driver:', error);
        });
    } else if (userId) {
      setResolvedUserId(userId);
    }
  }, [driverId, userId, dataProvider]);

  // Use resolved userId if available
  const finalUserId = resolvedUserId || userId;

  console.log('DriverWizard params:', { driverId, userId, finalUserId, urlParams: params });

  const steps = [
    {
      id: 'snapshot',
      title: 'Driver Profile',
      component: BasicDriverInfoStep,
    },
    {
      id: 'licences',
      title: 'Driving Licenses',
      component: DetailedDriverLicenseStep,
    },
    {
      id: 'education',
      title: 'Education',
      component: DetailedEducationStep,
    },
    {
      id: 'employment',
      title: 'Employment',
      component: DetailedEmploymentStep,
    },
    {
      id: 'certification',
      title: 'Certifications',
      component: DetailedCertificationStep,
    },
    {
      id: 'accountability',
      title: 'Accountability',
      component: DocumentsStep,
    },
  ];

  const handleComplete = () => {
    // Check if user is standalone (no tenants)
    const isStandalone = localStorage.getItem('isStandalone') === 'true';

    if (isStandalone) {
      // Standalone user - go back to standalone dashboard
      navigate('/standalone-dashboard');
    } else if (finalUserId) {
      // Tenant user - navigate back to their driver profile page with snapshot tab
      navigate(`/users/${finalUserId}/show/1?tab=snapshot`);
    } else {
      // Fallback to users list
      navigate('/users');
    }
  };

  return (
    <DriverProfileWizardFullscreen
      steps={steps}
      onComplete={handleComplete}
      driverId={driverId}
      userId={finalUserId}
    />
  );
};